const TRUSTED_DEVICE_TOKEN_KEY = "admin.trusted_device_token";
const TRUSTED_DEVICE_FINGERPRINT_SEED_KEY = "admin.trusted_device_fingerprint_seed";
const TRUSTED_DEVICE_LOCAL_TTL_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

type StoredTrustedDeviceToken = {
  token: string;
  createdAt: number;
  expiresAt: number;
};

function safeStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  return window.localStorage ?? null;
}

function getOrCreateFingerprintSeed(): string {
  const storage = safeStorage();
  if (!storage) {
    return crypto.randomUUID();
  }

  const existing = storage.getItem(TRUSTED_DEVICE_FINGERPRINT_SEED_KEY);
  if (existing && existing.trim().length > 0) return existing;

  const next = crypto.randomUUID();
  storage.setItem(TRUSTED_DEVICE_FINGERPRINT_SEED_KEY, next);
  return next;
}

export function getTrustedDeviceFingerprint(): string {
  if (typeof window === "undefined") {
    return "server-runtime";
  }

  const seed = getOrCreateFingerprintSeed();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? "unknown";
  const language = navigator.language ?? "unknown";
  const platform = navigator.platform ?? "unknown";
  const userAgent = navigator.userAgent ?? "unknown";

  return `${seed}|${timezone}|${language}|${platform}|${userAgent}`;
}

export function generateTrustedDeviceToken(): string {
  return `${crypto.randomUUID()}-${crypto.randomUUID()}`;
}

export function getTrustedDeviceToken(): string | null {
  const storage = safeStorage();
  if (!storage) return null;
  const raw = storage.getItem(TRUSTED_DEVICE_TOKEN_KEY);
  if (!raw || raw.trim().length === 0) return null;

  // Backward compatibility for previous plain-string format.
  if (!raw.trim().startsWith("{")) {
    return raw.trim();
  }

  try {
    const parsed = JSON.parse(raw) as StoredTrustedDeviceToken;
    if (typeof parsed.token !== "string" || parsed.token.trim().length === 0) {
      clearTrustedDeviceToken();
      return null;
    }
    if (typeof parsed.expiresAt !== "number" || parsed.expiresAt <= Date.now()) {
      clearTrustedDeviceToken();
      return null;
    }
    return parsed.token;
  } catch {
    clearTrustedDeviceToken();
    return null;
  }
}

export function setTrustedDeviceToken(token: string, ttlDays = TRUSTED_DEVICE_LOCAL_TTL_DAYS) {
  const storage = safeStorage();
  if (!storage) return;
  const ttlMs = Math.max(1, Math.floor(ttlDays)) * DAY_MS;
  const payload: StoredTrustedDeviceToken = {
    token,
    createdAt: Date.now(),
    expiresAt: Date.now() + ttlMs,
  };
  storage.setItem(TRUSTED_DEVICE_TOKEN_KEY, JSON.stringify(payload));
}

export function clearTrustedDeviceToken() {
  const storage = safeStorage();
  if (!storage) return;
  storage.removeItem(TRUSTED_DEVICE_TOKEN_KEY);
}
